import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { BrainCircuit } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BIAS = {
  long: { label: "LONG", cls: "text-[#00FF41] border-[#00FF41]/40" },
  watch: { label: "WATCH", cls: "text-gold border-gold/40" },
  avoid: { label: "AVOID", cls: "text-[#FF003C] border-[#FF003C]/40" },
};

export default function AiDesk() {
  const [desk, setDesk] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/ai/desk`)
      .then(({ data }) => setDesk(data))
      .catch(() => setDesk(null));
  }, []);

  if (!desk) return null;
  const bias = BIAS[desk.bias] || BIAS.watch;
  const conviction = Math.min(10, Math.max(1, Number(desk.conviction) || 5));

  return (
    <section data-testid="ai-desk" className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden border border-gold/25 bg-surface p-8 sm:p-12"
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <p className="mb-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-gold">
              <BrainCircuit className="h-4 w-4" />
              The AI Desk · Today's read
            </p>
            <h2 className="font-serif text-4xl font-light tracking-tight text-white sm:text-5xl" data-testid="ai-desk-headline">
              {desk.headline}
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-white/55" data-testid="ai-desk-reasoning">
              {desk.reasoning}
            </p>
            <p className="mt-6 text-[10px] uppercase tracking-[0.2em] text-white/30">
              {desk.disclaimer}
            </p>
          </div>
          <div className="flex shrink-0 flex-row gap-8 lg:flex-col lg:gap-6 lg:text-right">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Asset</p>
              <p className="mt-1 font-serif text-4xl font-light text-gold" data-testid="ai-desk-symbol">
                {desk.symbol}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Bias</p>
              <span
                data-testid="ai-desk-bias"
                className={`mt-2 inline-block border px-4 py-1.5 text-xs uppercase tracking-[0.25em] ${bias.cls}`}
              >
                {bias.label}
              </span>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Conviction</p>
              <div className="mt-2 flex gap-1 lg:justify-end" data-testid="ai-desk-conviction">
                {Array.from({ length: 10 }, (_, i) => (
                  <span
                    key={i}
                    className={`h-4 w-1.5 ${i < conviction ? "bg-gold" : "bg-white/10"}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
